import React, { useEffect, useRef, useState } from "react";
import { api } from "./api";
import { leerMisReportes, agregarMiReporte, actualizarEstado, borrarMisReportes } from "./misReportes";

// Reportes ciudadanos: falla de un objeto o algo que no está registrado. Anónimo, o dejando el celular (opcional, con
// consentimiento). Nunca cambia los datos oficiales: el reporte queda en una bandeja para que alguien lo revise.
const SITEKEY = process.env.REACT_APP_TURNSTILE_SITEKEY || "";   // Cloudflare Turnstile (si está vacío no se pide)

const TIPOS_OBJETO = [
    { id: "FALLA", texto: "No funciona" },
    { id: "DANO", texto: "Dañado o quebrado" },
    { id: "PELIGRO", texto: "Es un peligro" },
    { id: "VANDALIZADO", texto: "Vandalizado" },
    { id: "OTRO", texto: "Otro" },
];
const ESTADOS_REPORTE = {
    NUEVO: "Recibido: aún no lo revisamos",
    EN_REVISION: "En revisión",
    RESUELTO: "Resuelto",
    DESCARTADO: "Revisado y cerrado sin cambios",
};

// ---- Fotos ----------------------------------------------------------------------------------------------------
// El navegador las reduce antes de enviarlas: lado mayor 1280 px y JPEG. Eso baja una foto de celular de varios MB a
// ~200 KB y además elimina sus metadatos (la ubicación EXIF del celular); el servidor vuelve a limpiarlas igual.
const MAX_FOTOS = 3;
const LADO_FOTO = 1280;
const MAX_B64 = 850 * 1024;   // el servidor rechaza más de ~900 KB por foto

function cargarImagen(archivo) {
    if (window.createImageBitmap) {
        return createImageBitmap(archivo, { imageOrientation: "from-image" }).catch(() => cargarConImg(archivo));
    }
    return cargarConImg(archivo);
}
function cargarConImg(archivo) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(archivo);
        const img = new Image();
        img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("No pudimos leer esa foto.")); };
        img.src = url;
    });
}
async function reducirFoto(archivo) {
    if (!/^image\//.test(archivo.type || "image/")) throw new Error("Eso no es una foto.");
    const img = await cargarImagen(archivo);
    const w0 = img.width || img.naturalWidth, h0 = img.height || img.naturalHeight;
    if (!w0 || !h0) throw new Error("No pudimos leer esa foto.");
    for (const [lado, calidad] of [[LADO_FOTO, 0.8], [1024, 0.7], [800, 0.6]]) {
        const k = Math.min(1, lado / Math.max(w0, h0));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(w0 * k); canvas.height = Math.round(h0 * k);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        const url = canvas.toDataURL("image/jpeg", calidad);
        if (url.length <= MAX_B64) return url;
    }
    throw new Error("Esa foto es demasiado pesada.");
}

const ESTADOS_CORTO = { NUEVO: "Recibido", EN_REVISION: "En revisión", RESUELTO: "Resuelto", DESCARTADO: "Cerrado" };

function useTurnstile(contenedor, alToken) {
    useEffect(() => {
        if (!SITEKEY || !contenedor.current) return undefined;
        let id = null, vivo = true;
        const pintar = () => {
            if (!vivo || !window.turnstile || !contenedor.current) return;
            id = window.turnstile.render(contenedor.current, {
                sitekey: SITEKEY, callback: (t) => alToken(t), "expired-callback": () => alToken(""), "error-callback": () => alToken(""),
            });
        };
        if (window.turnstile) pintar();
        else {
            let s = document.getElementById("turnstile-script");
            if (!s) {
                s = document.createElement("script");
                s.id = "turnstile-script"; s.async = true; s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
                document.head.appendChild(s);
            }
            s.addEventListener("load", pintar);
        }
        return () => { vivo = false; if (id !== null && window.turnstile) window.turnstile.remove(id); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}

// contexto: { objeto: {id_objeto, nombre, codigo} } o { lugar: {lat, lon} }; tipos: tipos de objeto de la zona (para "¿qué es?")
export function FormularioReporte({ contexto, tipos, onCerrar }) {
    const esLugar = !!contexto.lugar;
    const [tipo, setTipo] = useState(esLugar ? "NO_REGISTRADO" : "FALLA");
    const [xrowidtip, setXrowidtip] = useState("");
    const [descripcion, setDescripcion] = useState("");
    const [contacto, setContacto] = useState("anonimo");
    const [celular, setCelular] = useState("");
    const [consentimiento, setConsentimiento] = useState(false);
    const [web, setWeb] = useState("");   // campo trampa para bots: las personas no lo ven
    const [token, setToken] = useState("");
    const [enviando, setEnviando] = useState(false);
    const [error, setError] = useState(null);
    const [codigo, setCodigo] = useState(null);
    const [fotos, setFotos] = useState([]);                 // data URLs JPEG ya reducidas
    const [procesando, setProcesando] = useState(false);
    const [errorFoto, setErrorFoto] = useState(null);
    const widget = useRef(null);
    useTurnstile(widget, setToken);

    const agregarFotos = async (e) => {
        const archivos = Array.from(e.target.files || []);
        e.target.value = "";   // permite volver a elegir la misma foto
        if (!archivos.length) return;
        setErrorFoto(null); setProcesando(true);
        const nuevas = [];
        try {
            for (const a of archivos.slice(0, MAX_FOTOS - fotos.length)) nuevas.push(await reducirFoto(a));
            if (archivos.length > MAX_FOTOS - fotos.length) setErrorFoto(`Solo se pueden adjuntar ${MAX_FOTOS} fotos.`);
        } catch (err) { setErrorFoto(err.message); }
        setFotos((prev) => [...prev, ...nuevas].slice(0, MAX_FOTOS));
        setProcesando(false);
    };
    const quitarFoto = (i) => { setErrorFoto(null); setFotos((prev) => prev.filter((_, k) => k !== i)); };

    const enviar = (e) => {
        e.preventDefault();
        setError(null);
        if (descripcion.trim().length < 5) { setError("Cuéntanos qué pasa (al menos 5 letras)."); return; }
        if (contacto === "celular" && !consentimiento) { setError("Para dejar tu celular marca la casilla de autorización."); return; }
        setEnviando(true);
        const param = {
            tipo, descripcion: descripcion.trim(), web, turnstile: token, ...(fotos.length ? { fotos } : {}),
            ...(esLugar ? { gpslat: contexto.lugar.lat, gpslon: contexto.lugar.lon, xrowidtip: xrowidtip || undefined } : { xrowidobj: contexto.objeto.id_objeto }),
            ...(contacto === "celular" ? { celular: celular.trim(), consentimiento: true } : {}),
        };
        api.reportar(param)
            .then((o) => {
                agregarMiReporte({ codigo: o.codigo, tipo, donde: esLugar ? "Lugar marcado en el mapa" : (contexto.objeto.nombre || contexto.objeto.codigo) });
                setCodigo(o.codigo); setEnviando(false);
            })
            .catch((err) => { setError(err.message.replace(/^Error CODE:\d+,\s*/, "")); setEnviando(false); });
    };

    return (
        <div className="hoja-fondo" role="dialog" aria-modal="true" aria-label="Reportar un problema" onMouseDown={(e) => { if (e.target === e.currentTarget) onCerrar(); }}>
            <div className="hoja">
                <header className="hoja-cab">
                    <h2>{esLugar ? "Reportar algo que falta" : "Reportar un problema"}</h2>
                    <button type="button" className="icono" onClick={onCerrar} aria-label="Cerrar">×</button>
                </header>

                {codigo ? (
                    <div className="hoja-cuerpo">
                        <p className="ok-grande">¡Gracias! Recibimos tu reporte.</p>
                        <p>Tu código de seguimiento es:</p>
                        <p className="codigo">{codigo}</p>
                        <p className="ayuda">Guárdalo: con él puedes consultar el estado cuando quieras, incluso sin haber dejado tus datos. Un reporte no cambia el mapa hasta que una persona lo revisa.</p>
                        <button type="button" className="boton" onClick={onCerrar}>Listo</button>
                    </div>
                ) : (
                    <form className="hoja-cuerpo" onSubmit={enviar}>
                        {esLugar ? (
                            <p className="ayuda">Lugar marcado: {contexto.lugar.lat.toFixed(5)}, {contexto.lugar.lon.toFixed(5)}. Cuéntanos qué hay o qué falta ahí.</p>
                        ) : (
                            <p className="ayuda">Objeto: <strong>{contexto.objeto.nombre || contexto.objeto.codigo}</strong></p>
                        )}

                        {!esLugar && (
                            <fieldset className="campo">
                                <legend>¿Qué pasa?</legend>
                                <div className="atajos">
                                    {TIPOS_OBJETO.map((t) => (
                                        <button key={t.id} type="button" className={"atajo" + (tipo === t.id ? " activo" : "")} aria-pressed={tipo === t.id} onClick={() => setTipo(t.id)}>{t.texto}</button>
                                    ))}
                                </div>
                            </fieldset>
                        )}
                        {esLugar && (
                            <label className="campo">¿Qué es?
                                <select value={xrowidtip} onChange={(e) => setXrowidtip(e.target.value)}>
                                    <option value="">Otro / no sé</option>
                                    {tipos.map((t) => <option key={t.id_tipo} value={t.id_tipo}>{t.nombre}</option>)}
                                </select>
                            </label>
                        )}

                        <label className="campo">Cuéntanos más
                            <textarea rows={4} maxLength={1000} value={descripcion} onChange={(e) => setDescripcion(e.target.value)}
                                placeholder={esLugar ? "Ej.: falta un grifo en esta esquina, hay un hoyo en la calzada…" : "Ej.: la luminaria lleva una semana apagada…"} required />
                            <span className="contador">{descripcion.length}/1000</span>
                        </label>

                        <fieldset className="campo">
                            <legend>Fotos (opcional, hasta {MAX_FOTOS})</legend>
                            <div className="fotos-botones">
                                <label className={"atajo boton-foto" + (fotos.length >= MAX_FOTOS || procesando ? " off" : "")}>
                                    📷 Tomar foto
                                    <input type="file" accept="image/*" capture="environment" onChange={agregarFotos} disabled={fotos.length >= MAX_FOTOS || procesando} />
                                </label>
                                <label className={"atajo boton-foto" + (fotos.length >= MAX_FOTOS || procesando ? " off" : "")}>
                                    🖼 Elegir de la galería
                                    <input type="file" accept="image/*" multiple onChange={agregarFotos} disabled={fotos.length >= MAX_FOTOS || procesando} />
                                </label>
                            </div>
                            {procesando && <span className="ayuda">Preparando la foto…</span>}
                            {fotos.length > 0 && (
                                <div className="fotos-mini">
                                    {fotos.map((f, i) => (
                                        <div className="foto-mini" key={i}>
                                            <img src={f} alt={`Foto ${i + 1} del reporte`} />
                                            <button type="button" onClick={() => quitarFoto(i)} aria-label={`Quitar la foto ${i + 1}`}>×</button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {errorFoto && <span className="error" role="alert">{errorFoto}</span>}
                            <span className="ayuda">Solo las ve el equipo que revisa los reportes: no se publican. Procura que no salgan personas ni patentes.</span>
                        </fieldset>

                        <fieldset className="campo">
                            <legend>¿Quieres que te avisemos?</legend>
                            <label className="radio"><input type="radio" name="contacto" checked={contacto === "anonimo"} onChange={() => setContacto("anonimo")} /> No, prefiero reportar de forma anónima</label>
                            <label className="radio"><input type="radio" name="contacto" checked={contacto === "celular"} onChange={() => setContacto("celular")} /> Sí, dejo mi celular</label>
                            {contacto === "celular" && (<>
                                <input type="tel" inputMode="tel" autoComplete="tel" placeholder="+56 9 1234 5678" value={celular} onChange={(e) => setCelular(e.target.value)} required />
                                <label className="radio chico">
                                    <input type="checkbox" checked={consentimiento} onChange={(e) => setConsentimiento(e.target.checked)} />
                                    Autorizo que usen mi celular solo para avisarme sobre este reporte. Solo lo verá el equipo que lo revisa, no se publica, y puedo pedir que lo borren escribiendo a checklist@harrys.cl.
                                </label>
                            </>)}
                        </fieldset>

                        {/* campo trampa: invisible para personas */}
                        <div className="trampa" aria-hidden="true"><label>Sitio web <input type="text" tabIndex={-1} autoComplete="off" value={web} onChange={(e) => setWeb(e.target.value)} /></label></div>
                        {SITEKEY && <div ref={widget} className="turnstile" />}

                        {error && <p className="error" role="alert">{error}</p>}
                        <button type="submit" className="boton" disabled={enviando || procesando || (SITEKEY && !token)}>{enviando ? "Enviando…" : "Enviar reporte"}</button>
                    </form>
                )}
            </div>
        </div>
    );
}

const TIPO_TXT = { FALLA: "No funciona", DANO: "Dañado o quebrado", PELIGRO: "Peligro", VANDALIZADO: "Vandalizado", OTRO: "Otro", NO_REGISTRADO: "Algo que falta" };

export function ConsultaReporte({ onCerrar }) {
    const [codigo, setCodigo] = useState("");
    const [mios, setMios] = useState(() => leerMisReportes());
    // al abrir se actualiza el estado de los que aún no están cerrados (máx. 10, uno a uno)
    useEffect(() => {
        let vivo = true;
        (async () => {
            for (const r of leerMisReportes().filter((x) => x.estado !== "RESUELTO" && x.estado !== "DESCARTADO").slice(0, 10)) {
                try {
                    const o = await api.seguimiento(r.codigo);
                    if (!vivo) return;
                    if (o.reporte && o.reporte.estado !== r.estado) { actualizarEstado(r.codigo, o.reporte.estado); setMios(leerMisReportes()); }
                } catch (e) { /* sin red: queda el último estado conocido */ }
            }
        })();
        return () => { vivo = false; };
    }, []);
    const olvidar = () => { borrarMisReportes(); setMios([]); };
    const [cargando, setCargando] = useState(false);
    const [resultado, setResultado] = useState(undefined);   // undefined = aún no consulta; null = no existe
    const [error, setError] = useState(null);
    const buscar = (e) => {
        e.preventDefault();
        setError(null); setResultado(undefined); setCargando(true);
        api.seguimiento(codigo.trim())
            .then((o) => { setResultado(o.reporte); setCargando(false); })
            .catch((err) => { setError(err.message.replace(/^Error CODE:\d+,\s*/, "")); setCargando(false); });
    };
    const fecha = (f) => (f ? new Date(f).toLocaleDateString("es-CL", { day: "numeric", month: "long", year: "numeric" }) : "");
    return (
        <div className="hoja-fondo" role="dialog" aria-modal="true" aria-label="Consultar un reporte" onMouseDown={(e) => { if (e.target === e.currentTarget) onCerrar(); }}>
            <div className="hoja">
                <header className="hoja-cab">
                    <h2>Consultar un reporte</h2>
                    <button type="button" className="icono" onClick={onCerrar} aria-label="Cerrar">×</button>
                </header>
                <form className="hoja-cuerpo" onSubmit={buscar}>
                    <label className="campo">Código de seguimiento
                        <input type="text" value={codigo} onChange={(e) => setCodigo(e.target.value.toUpperCase())} placeholder="R-8F3K2" maxLength={7} autoCapitalize="characters" required />
                    </label>
                    <button type="submit" className="boton" disabled={cargando}>{cargando ? "Buscando…" : "Consultar"}</button>
                    {error && <p className="error" role="alert">{error}</p>}
                    {resultado === null && <p className="error" role="status">No encontramos un reporte con ese código en este sitio.</p>}
                    {resultado && (
                        <div className="resultado" role="status">
                            <p className="ok-grande">{ESTADOS_REPORTE[resultado.estado] || resultado.estado}</p>
                            <p className="ayuda">Reporte {resultado.codigo} · enviado el {fecha(resultado.finserta)}{resultado.fresuelve ? ` · cerrado el ${fecha(resultado.fresuelve)}` : ""}</p>
                        </div>
                    )}
                    {mios.length > 0 && (
                        <div className="mis-reportes">
                            <h3>Mis reportes en este dispositivo</h3>
                            <ul>
                                {mios.map((r) => (
                                    <li key={r.codigo}>
                                        <button type="button" className="enlace-bt" onClick={() => setCodigo(r.codigo)} title="Usar este código">{r.codigo}</button>
                                        <span className={"estado-rep " + r.estado}>{ESTADOS_CORTO[r.estado] || r.estado}</span>
                                        <span className="ayuda">{TIPO_TXT[r.tipo] || r.tipo} · {r.donde} · {fecha(r.fecha)}</span>
                                    </li>
                                ))}
                            </ul>
                            <p className="ayuda">Se guardan solo en este celular o computador, no en una cuenta. <button type="button" className="enlace-bt" onClick={olvidar}>Borrar esta lista</button></p>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
}
